import type { CatalogSearchResult } from '../../../data/catalogSearch';
import { cn } from '../../../lib/utils';
import { usdCost } from '../../../lib/format';
import { relevanceLabel, relevancePct } from '../../../lib/catalogSearch';
import { platformInfoOf } from '../../../lib/catalogMap';
import { platformIdOf } from '../../../lib/catalogPlatform';
import { displayPrice, quotedRateLabel, quotedRateUsd } from '../../../lib/pricing';
import { PlatformMark } from '../../../components/PlatformMark';
import { m } from '../../../paraglide/messages';

export interface CatalogSearchRowProps {
  result: CatalogSearchResult;
  /** stable ARIA option id, wiring the combobox active-descendant to this row */
  id: string;
  selected: boolean;
  /** show the relevance bar / best-match tag (results phase only) */
  showRelevance: boolean;
  /** this is the top-ranked result (renders "Best match" instead of a bar) */
  best: boolean;
  onSelect: () => void;
  onHover: () => void;
}

/** One catalog endpoint row in the palette: the platform's real brand logo (or
 *  its category-tinted icon fallback), the SKU name + slug + description, the USD
 *  price, and - in results phase - a relevance bar (or "Best match" on the top
 *  hit). Uses a div (not a button) so any nested control stays valid. */
export const CatalogSearchRow = ({
  result,
  id,
  selected,
  showRelevance,
  best,
  onSelect,
  onHover,
}: CatalogSearchRowProps) => {
  const price = displayPrice(result.pricing.from);
  const platformId = platformIdOf(result.slug);
  return <div
    role="option"
    id={id}
    aria-selected={selected}
    onClick={onSelect}
    onMouseMove={onHover}
    className={cn(
      'flex w-full cursor-pointer flex-wrap items-start gap-3 px-4 py-2.5',
      selected
        ? 'bg-accent-soft shadow-[inset_2px_0_0_var(--accent)]'
        : 'hover:bg-surface-2',
    )}
  >
    <span className="mt-0.5 grid size-7 shrink-0 place-items-center">
      <PlatformMark
        id={platformId}
        icon={platformInfoOf(result.slug).icon}
        category={result.category}
        size={28}
        iconSize={16}
        radius={7}
      />
    </span>
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-baseline gap-x-2 sm:flex-nowrap">
        <b className="truncate text-14 font-semibold tracking-[-0.01em] text-text">
          {result.name}
        </b>
        <span className="mono truncate text-11 text-text-3">{result.slug}</span>
      </div>
      <div className="truncate text-13 text-text-2">{result.description}</div>
    </div>
    <div className="shrink-0 text-right">
      <div
        className={`mono text-13 font-semibold ${price.metered ? 'text-meter' : 'text-accent'}`}
      >
        {usdCost(quotedRateUsd(price))}
        <span className="text-11 font-normal text-text-3"> {quotedRateLabel(price)}</span>
      </div>
      {showRelevance ? (
        best ? (
          <div className="mt-1.5">
            <span className="mono text-10 font-semibold tracking-[0.06em] text-pos uppercase">
              {m.search_best_match()}
            </span>
          </div>
        ) : (
          <div className="mt-1.5 flex items-center justify-end gap-1.5">
            <span className="h-1 w-9 overflow-hidden rounded-2 bg-line">
              <span
                className="block h-full rounded-2 bg-accent"
                style={{ width: `${relevancePct(result.relevance)}%`, opacity: selected ? 0.9 : 0.55 }}
              />
            </span>
            <span className="mono text-10 text-text-3">{relevanceLabel(result.relevance)}</span>
          </div>
        )
      ) : null}
    </div>
  </div>;
};
